// Written by Claude: pure rules for the Launch sends tab, kept out of the
// components so the tests can pin them. web-backend enforces every one of these
// (docs/launch-send-console.md: 409 / 400 / 403); the UI only stops a press the
// server would refuse, and never claims a figure it was not given.

import type { Tone } from "@/lib/tone";

import { CampaignsError, POLL_MS, type LaunchMode, type TestPress, type Wave, type WaveState } from "./api";

const STALE = "These figures may be out of date. Reload before sending.";
const PRESSED = "A press was just made and has not shown here yet. Reload in a minute before pressing again.";

/** After a 202 the tab keeps polling this long even if the re-read still says `ready`. */
export const PRESS_POLL_WINDOW_MS = 60_000;

/** True while the wave's live press OR its latest seed / dry press is starting or running. */
export function isRunning(wave: Wave): boolean {
  const test = wave.last_test?.state;
  return wave.state === "running" || test === "running" || test === "starting";
}

/**
 * Why Send is not available for this wave, or null when it is. In dry mode a
 * press is a preview that never counts, so only a running press blocks it (a dry
 * preview of wave 2 needs no live wave 1). In live mode a wave sends once and
 * waits only for the wave its `after` names to have sent ok. List order is never
 * a prerequisite: domain_switch_wave1 follows launch_wave2 in the list but does
 * not wait on it. With no `after` the server's 409 is the only order check.
 */
export function sendBlockReason(
  wave: Wave,
  waves: Wave[],
  mode: LaunchMode,
  stale = false,
  /** Until when a press of this wave (a 202, or a failure that may have started a run) holds Send shut. */
  pressedUntil = 0,
  now = Date.now(),
): string | null {
  if (isRunning(wave)) return "Sending now.";
  // Held at tab level, not in the dialog: Cancel + reopen must not re-open Send before the run shows.
  if (now < pressedUntil) return PRESSED;
  if (stale) return STALE;
  if (wave.audience === null) return "The audience could not be read. Reload before sending.";
  if (!modeLine(mode).live) return null;
  if (wave.state === "sent") return "Already sent. Each wave sends once.";
  if (!wave.after) return null;
  const prerequisite = waves.find((w) => w.wave === wave.after);
  if (prerequisite?.state === "sent") return null;
  return `Waits until ${prerequisite?.label ?? wave.after} has finished sending.`;
}

/** Why the seed test is not available, or null when it is. A seed only reaches one damien+ address. */
export function seedBlockReason(wave: Wave, stale = false): string | null {
  if (isRunning(wave)) return "Wait for the running send to finish.";
  if (stale) return STALE;
  return null;
}

/** The confirm dialog fires only when the typed text is the wave key, exactly. */
export function confirmMatches(typed: string, wave: Wave): boolean {
  return typed.trim() === wave.wave;
}

export function anyRunning(waves: Wave[] | undefined): boolean {
  return (waves ?? []).some(isRunning);
}

/** The re-read interval: POLL_MS while any press runs or inside the post-202 window, else no polling. */
export function pollInterval(waves: Wave[] | undefined, pollUntil: number, now: number): number | false {
  return anyRunning(waves) || now < pollUntil ? POLL_MS : false;
}

export const STATE_LABEL: Record<WaveState, string> = {
  ready: "Ready",
  running: "Sending",
  sent: "Sent",
  failed: "Failed",
};

/** The shared tone for a wave state; `tonePillClass` turns it into the pill. */
export function stateTone(state: WaveState): Tone {
  if (state === "sent") return "ok";
  if (state === "failed") return "critical";
  if (state === "running") return "watch";
  return "neutral";
}

const COUNT = new Intl.NumberFormat("en-GB");

/** A count, or an em dash when the backend does not know it yet (never a false 0). */
export function fmtCount(n: number | null | undefined): string {
  return typeof n === "number" ? COUNT.format(n) : "—";
}

/** "EN 30 · DE 18", largest first; "No language split" when the backend sent none. */
export function languageLine(byLanguage: Record<string, number> | null | undefined): string {
  const entries = Object.entries(byLanguage ?? {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (entries.length === 0) return "No language split";
  return entries.map(([lang, n]) => `${lang.toUpperCase()} ${fmtCount(n)}`).join(" · ");
}

/** "12 held, not sent (DE 5 · FR 4 · ES 3)", or null when nobody is held. */
export function heldLine(wave: Wave): string | null {
  if (!wave.held || wave.held.count === 0) return null;
  return `${fmtCount(wave.held.count)} held, not sent (${languageLine(wave.held.by_language)})`;
}

const WHEN = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
});

export function fmtWhen(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : WHEN.format(d);
}

function whenLine(press: { started_at: string | null; finished_at: string | null }): string {
  const started = fmtWhen(press.started_at);
  const finished = fmtWhen(press.finished_at);
  return finished ? `finished ${finished}` : started ? `started ${started}` : "time not recorded";
}

/** One line on the last live execution: its id and when it ran. */
export function executionLine(wave: Wave): string {
  if (!wave.execution) return "Not sent yet";
  return `${wave.execution} · ${whenLine(wave)}`;
}

/** One line on the latest seed or dry-run press, or null when there was none. */
export function testLine(test: TestPress | null | undefined): string | null {
  if (!test) return null;
  const kind = test.kind === "seed" ? "Seed test" : "Dry run";
  const outcome =
    test.state === "running" || test.state === "starting"
      ? "sending"
      : test.state === "start_failed"
        ? "could not start"
        : STATE_LABEL[test.state].toLowerCase();
  const sent = typeof test.sent === "number" ? `, ${fmtCount(test.sent)} sent` : "";
  return `${kind} ${outcome}${sent} · ${test.execution} · ${whenLine(test)}`;
}

/** What a press does, in plain words. Anything but prod + live is a warning: nothing reaches customers. */
export function modeLine(mode: LaunchMode): { live: boolean; text: string } {
  if (mode.email_env === "prod" && !mode.dry_run) {
    return { live: true, text: "Live: a press emails real customers from the production sender." };
  }
  const sender = mode.email_env === "prod" ? "Production sender" : "Dev sender";
  return { live: false, text: `${sender}, dry run ${mode.dry_run ? "on" : "off"}: a press does not reach customers.` };
}

function reason(error: CampaignsError): string {
  return error.body?.error ?? "";
}

/**
 * What each refusal means, by the server's reason code (web-backend
 * docs/launch-send-console.md, the portal proxy and the portal authz). Matched
 * on the CODE, never on the status alone: one status carries several reasons.
 */
const SEND_REASONS: Record<string, string> = {
  confirm_mismatch: "The confirmation did not match the wave name. Nothing was sent.",
  json_only: "The send was refused as malformed (not JSON). Reload and try again. Nothing was sent.",
  not_approver: "You are not on the approver list for launch sends. Nothing was sent.",
  forbidden: "You do not have edit access to Campaigns. Nothing was sent.",
  bad_origin: "The send must come from os.ketosource.co. Nothing was sent.",
  unknown_wave: "The backend does not know this wave. Reload the page. Nothing was sent.",
  not_routed: "Launch sends are not switched on yet. Nothing was sent.",
  running: "This wave is sending now. Nothing new was started.",
  busy: "Another press is starting for this wave. Wait a moment and reload. Nothing new was started.",
  already_sent: "This wave has already been sent. Nothing new was started.",
  wave1_not_sent: "This wave waits until the wave it follows has finished sending. Nothing was sent.",
  start_failed: "Cloud Run refused to start the send. Nothing was sent or recorded. Try again.",
  start_unconfirmed:
    "Start not confirmed: the wave is locked until the run is found. Do not press again; reload in a minute to see its state.",
  not_configured: "Launch sends are not connected yet (backend address not set). Nothing was sent.",
  portal_authz_unavailable: "The portal permission check is unavailable. Nothing was sent. Try again in a minute.",
};

/** A 5xx with no reason code came from a gateway (Cloudflare 524 timeout, Cloud Run 502/504), not from web-backend. */
function gatewayFault(error: CampaignsError): boolean {
  return error.status >= 500 && reason(error) === "";
}

const NOT_CONFIRMED =
  "Not confirmed: the send may be running. Do not press again; reload in a minute to see its state.";

/** No HTTP answer at all: a network drop, or an expired Access session redirecting the call to the sign-in page. */
const NO_ANSWER =
  "Not confirmed: the send may be running, or your sign-in has expired. Do not press again; reload the page and sign in again if asked.";

/**
 * True when a failed press may still have started a run (no answer, a gateway
 * timeout, or web-backend's own start_unconfirmed), so the tab must keep
 * polling exactly as it does after a 202.
 */
export function mayHaveStarted(error: unknown): boolean {
  if (!(error instanceof CampaignsError)) return true;
  return reason(error) === "start_unconfirmed" || gatewayFault(error);
}

/** The message a failed press shows. An unknown reason gets a generic line naming the status and code. */
export function sendErrorMessage(error: unknown): string {
  if (!(error instanceof CampaignsError)) return NO_ANSWER;
  const code = reason(error);
  if (Object.hasOwn(SEND_REASONS, code)) return SEND_REASONS[code];
  if (error.status === 401) return "Your sign-in has expired. Reload the page and sign in again. Nothing was sent.";
  if (gatewayFault(error)) return NOT_CONFIRMED;
  return `The send was refused (error ${error.status}${code ? `, ${code}` : ""}). Reload to see the wave's state.`;
}

/** The message a failed read shows. */
export function loadErrorMessage(error: unknown): string {
  if (error instanceof CampaignsError) {
    const code = reason(error);
    if (error.status === 401) return "Your sign-in has expired. Reload the page and sign in again.";
    if (code === "not_configured") return "Launch sends are not connected yet (backend address not set).";
    if (code === "portal_authz_unavailable") return "The portal permission check is unavailable. Try again in a minute.";
    if (code === "not_routed" || error.status === 404) return "Launch sends are not switched on yet on the backend.";
    if (error.status === 403) return "You do not have access to Campaigns launch sends.";
  }
  return "The launch waves could not be read. Reload the page, and sign in again if asked.";
}
